/**
 * Secondary Menu Navigation Handler
 * Handles hover interactions for nested desktop navigation menus
 */

class SecondaryMenuHandler {
  constructor() {
    this.menuWrappers = document.querySelectorAll('.header-menu-item-wrapper');
    this.activeTimeout = null;
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupAccessibility();
  }

  setupEventListeners() {
    this.menuWrappers.forEach(wrapper => {
      const menuTrigger = wrapper.querySelector('.header-shortlink');
      const secondaryMenu = wrapper.querySelector('.header-secondary-menu');
      
      if (!menuTrigger || !secondaryMenu) return;

      // Mouse events
      wrapper.addEventListener('mouseenter', () => this.showMenu(secondaryMenu));
      wrapper.addEventListener('mouseleave', () => this.hideMenu(secondaryMenu));

      // Keyboard events for accessibility
      menuTrigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleMenu(secondaryMenu);
        } else if (e.key === 'Escape') {
          this.hideMenu(secondaryMenu);
        }
      });

      // Handle tertiary menu interactions
      const tertiaryTriggers = secondaryMenu.querySelectorAll('.secondary-menu-item');
      tertiaryTriggers.forEach(item => {
        const tertiaryMenu = item.querySelector('.tertiary-menu-panel');
        if (tertiaryMenu) {
          item.addEventListener('mouseenter', () => this.showTertiaryMenu(tertiaryMenu));
          item.addEventListener('mouseleave', () => this.hideTertiaryMenu(tertiaryMenu));
        }
      });
    });

    // Close all menus when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.header-menu-item-wrapper')) {
        this.hideAllMenus();
      }
    });

    // Handle window resize and scroll
    window.addEventListener('resize', () => this.hideAllMenus());
    window.addEventListener('scroll', () => this.repositionVisibleMenus());
  }

  setupAccessibility() {
    this.menuWrappers.forEach(wrapper => {
      const menuTrigger = wrapper.querySelector('.header-shortlink');
      const secondaryMenu = wrapper.querySelector('.header-secondary-menu');
      
      if (!menuTrigger || !secondaryMenu) return;

      // Add ARIA attributes
      const menuId = `secondary-menu-${Math.random().toString(36).substr(2, 9)}`;
      secondaryMenu.setAttribute('id', menuId);
      menuTrigger.setAttribute('aria-haspopup', 'true');
      menuTrigger.setAttribute('aria-expanded', 'false');
      menuTrigger.setAttribute('aria-controls', menuId);

      // Make menu items focusable
      const menuLinks = secondaryMenu.querySelectorAll('.secondary-menu-link, .tertiary-menu-link');
      menuLinks.forEach(link => {
        link.setAttribute('tabindex', '-1');
      });
    });
  }

  showMenu(menu) {
    clearTimeout(this.activeTimeout);
    
    // Calculate proper positioning
    const wrapper = menu.closest('.header-menu-item-wrapper');
    const trigger = wrapper.querySelector('.header-shortlink');
    const triggerRect = trigger.getBoundingClientRect();
    const headerHeight = document.querySelector('#MainHeader')?.offsetHeight || 80;
    const isLocalizationMenu = menu.classList.contains('header-localization-menu');
    
    // Position the menu directly below the trigger
    menu.style.position = 'fixed';
    menu.style.top = `${triggerRect.bottom + 4}px`;
    
    // Special positioning for localization menu (usually on the right)
    if (isLocalizationMenu) {
      menu.style.right = `${window.innerWidth - triggerRect.right}px`;
      menu.style.left = 'auto';
      menu.style.transform = 'none';
    } else {
      menu.style.left = `0px`;
      menu.style.transform = 'translateX(25%)';
    }
    
    menu.style.zIndex = '9999';
    
    menu.style.opacity = '1';
    menu.style.visibility = 'visible';
    menu.style.pointerEvents = 'auto';
    
    trigger.setAttribute('aria-expanded', 'true');
    
    // Focus first menu item for keyboard users
    const firstLink = menu.querySelector('.secondary-menu-link, .localization-link');
    if (firstLink && document.activeElement === trigger) {
      firstLink.focus();
    }
  }

  hideMenu(menu) {
    this.activeTimeout = setTimeout(() => {
      menu.style.opacity = '0';
      menu.style.visibility = 'hidden';
      menu.style.pointerEvents = 'none';
      
      const trigger = menu.closest('.header-menu-item-wrapper').querySelector('.header-shortlink');
      trigger.setAttribute('aria-expanded', 'false');
      
      // Hide any open tertiary menus
      const tertiaryMenus = menu.querySelectorAll('.tertiary-menu-panel');
      tertiaryMenus.forEach(tertiary => this.hideTertiaryMenu(tertiary));
    }, 100);
  }

  toggleMenu(menu) {
    const isVisible = menu.style.visibility === 'visible';
    if (isVisible) {
      this.hideMenu(menu);
    } else {
      this.hideAllMenus();
      this.showMenu(menu);
    }
  }

  showTertiaryMenu(menu) {
    menu.style.opacity = '1';
    menu.style.visibility = 'visible';
  }

  hideTertiaryMenu(menu) {
    menu.style.opacity = '0';
    menu.style.visibility = 'hidden';
  }

  hideAllMenus() {
    const allSecondaryMenus = document.querySelectorAll('.header-secondary-menu');
    allSecondaryMenus.forEach(menu => this.hideMenu(menu));
  }

  repositionVisibleMenus() {
    const visibleMenus = document.querySelectorAll('.header-secondary-menu[style*="visible"]');
    visibleMenus.forEach(menu => {
      const wrapper = menu.closest('.header-menu-item-wrapper');
      const trigger = wrapper.querySelector('.header-shortlink');
      const triggerRect = trigger.getBoundingClientRect();
      const isLocalizationMenu = menu.classList.contains('header-localization-menu');
      
      // Update position
      menu.style.top = `${triggerRect.bottom + 8}px`;
      
      if (isLocalizationMenu) {
        menu.style.right = `${window.innerWidth - triggerRect.right}px`;
        menu.style.left = 'auto';
      } else {
        menu.style.left = `${triggerRect.left + (triggerRect.width / 2)}px`;
      }
    });
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new SecondaryMenuHandler();
});

// Re-initialize on Shopify section reload (for theme editor)
document.addEventListener('shopify:section:load', () => {
  new SecondaryMenuHandler();
});
